import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { Alert, Button, Col, FormGroup, Input, Label, Row, UncontrolledTooltip } from 'reactstrap';
import { apiClient } from '../../utils/Api';
import { variantOptionLabel } from '../../components/Form/Select/Async/SelectVariant';
import SelectRack from '../../components/Form/Select/Async/SelectRack';
import { SimpleLineIcon } from '../../components/Icons';
import { ReturnListingFields } from './ReturnQcFields';
import { fetchRelistAskingPrice, initialReturnRelistForm } from './returnRelistForm';
import { returnErrorMessage } from './returnSale';
import { rackFitsQuantity } from '../../helpers/rackCapacity';

const ReturnRelistFields = ({ saleId, value, onChange, disabled, accountField }) => {
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    onChange(null);
    const load = async () => {
      const { data } = await apiClient().get(`/admins/sales/${saleId}`);
      const form = initialReturnRelistForm(data.data);
      const [variantResponse, sizesResponse] = await Promise.all([
        form.product && !form.product.name ? apiClient().get(`/admins/productvariants/${form.product.id}`) : null,
        form.product && form.size && !form.size.US ? apiClient().get(`/sizes/variants/${form.product.id}`) : null,
      ]);
      if (variantResponse) {
        const variant = variantResponse.data.data;
        form.product = {
          id: variant.id,
          name: variantOptionLabel(variant),
          category: String(variant.product.category.name).toLowerCase(),
          hide_box_info: variant.hide_box_info,
        };
      }
      if (sizesResponse) {
        const size = sizesResponse.data.data.find((item) => Number(item.id) === Number(form.size.id));
        form.size = size ? { id: size.id, US: size.US } : null;
      }
      if (form.asking_price_loading && form.product && form.size) {
        try {
          form.asking_price = await fetchRelistAskingPrice(form.product.id, form.size.id);
          form.asking_price_error = null;
        } catch (err) {
          form.asking_price = '0';
          form.asking_price_error = returnErrorMessage(err);
        }
        form.asking_price_loading = false;
      }
      if (!cancelled) onChange(form);
    };
    load().catch((err) => { if (!cancelled) setError(returnErrorMessage(err)); });
    return () => { cancelled = true; };
  }, [saleId, attempt, onChange]);

  const productId = value && value.product && value.product.id;
  const sizeId = value && value.size && value.size.id;
  const askingLoading = Boolean(value && value.asking_price_loading);
  useEffect(() => {
    if (!askingLoading) return undefined;
    if (!productId || !sizeId) {
      onChange((current) => (current ? { ...current, asking_price: '0', asking_price_loading: false } : current));
      return undefined;
    }
    let cancelled = false;
    fetchRelistAskingPrice(productId, sizeId).then((price) => {
      if (cancelled) return;
      onChange((current) => {
        if (!current || !current.asking_price_loading) return current;
        if (Number(current.product && current.product.id) !== Number(productId)) return current;
        if (Number(current.size && current.size.id) !== Number(sizeId)) return current;
        return { ...current, asking_price: price, asking_price_loading: false, asking_price_error: null };
      });
    }).catch((err) => {
      if (cancelled) return;
      onChange((current) => (current ? {
        ...current,
        asking_price: '0',
        asking_price_loading: false,
        asking_price_error: returnErrorMessage(err),
      } : current));
    });
    return () => { cancelled = true; };
  }, [askingLoading, productId, sizeId, onChange]);

  if (error) return (
    <Alert color="danger">
      {error}
      <Button type="button" size="sm" color="secondary" className="ml-2" onClick={() => setAttempt((count) => count + 1)}>Retry</Button>
    </Alert>
  );
  if (!value) return <p>Loading return details...</p>;

  const changeInput = (event) => {
    const { name, value: nextValue } = event.target;
    onChange((current) => ({
      ...current,
      [name]: nextValue,
      ...(name === 'quantity' && current.rack && !rackFitsQuantity(current.rack.count, current.rack.max, nextValue) ? { rack: null } : {}),
    }));
  };
  const changeProductFields = (update) => onChange((current) => {
    const next = typeof update === 'function' ? update(current) : update;
    const changed = (current.product && current.product.id) !== (next.product && next.product.id)
      || (current.size && current.size.id) !== (next.size && next.size.id);
    return changed ? { ...next, asking_price: '', asking_price_loading: true, asking_price_error: null } : next;
  });

  return (
    <fieldset disabled={disabled}>
      <Row>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label for="return-source-invoice">Source Invoice</Label>
        <Input id="return-source-invoice" value={value.source_invoice} disabled />
      </FormGroup>
      </Col>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label for="return-source-cg">Source CG</Label>
        <Input id="return-source-cg" value={value.source_cg} disabled />
      </FormGroup>
      </Col>
      </Row>
      <ReturnListingFields value={value} onChange={changeProductFields} disabled={disabled} idPrefix="return-relist" productOnly />
      <Row>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label id="return-asking-price-rules" for="return-asking-price">
          <span>Asking Price</span>
          <SimpleLineIcon iconType="question" className="ml-2" />
        </Label>
        <UncontrolledTooltip innerClassName="text-left" placement="right" target="return-asking-price-rules">
          Lowest Ask (asking price + API fee) for the selected product and size in Brand New condition with a Perfect box, minus Rp10,000.
          If no matching listing exists or the result is negative, show 0. The final price must be greater than 0 to save.
        </UncontrolledTooltip>
        <Input id="return-asking-price" name="asking_price" inputMode="numeric" value={value.asking_price} onChange={changeInput} disabled={disabled || value.asking_price_loading} />
        {value.asking_price_loading ? <small className="text-muted d-block">Looking up the lowest ask...</small> : null}
        {value.asking_price_error ? <small className="text-danger d-block">{value.asking_price_error}</small> : null}
      </FormGroup>
      </Col>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label for="return-purchase-price">Purchase Price</Label>
        <Input id="return-purchase-price" name="purchase_price" inputMode="decimal" value={value.purchase_price} onChange={changeInput} />
        <small className="text-muted">Product Price + API fee</small>
      </FormGroup>
      </Col>
      </Row>
      <Row>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label for="return-rack">Rack*</Label>
        <SelectRack
          key={`return-rack-${value.quantity}`}
          id="return-rack"
          value={value.rack}
          paramsApi={{ availables: true }}
          isAvailable
          sellQty={Number(value.quantity)}
          disabled={disabled || !Number.isSafeInteger(Number(value.quantity)) || Number(value.quantity) < 1}
          onSelectChange={(rack) => onChange((current) => ({ ...current, rack }))}
        />
      </FormGroup>
      </Col>
      <Col xs={12} md={6}>
        <FormGroup>
          <Label for="return-account">Kick Avenue Account*</Label>
          {accountField}
        </FormGroup>
      </Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <FormGroup>
            <Label for="return-quantity">Qty*</Label>
            <Input id="return-quantity" name="quantity" inputMode="numeric" value={value.quantity} onChange={changeInput} />
          </FormGroup>
        </Col>
      </Row>
      <ReturnListingFields value={value} onChange={onChange} disabled={disabled} idPrefix="return-relist" conditionOnly />
      <small className="text-muted">A new CGR will be generated when relisting is confirmed.</small>
    </fieldset>
  );
};

ReturnRelistFields.propTypes = {
  saleId: propTypes.number.isRequired,
  value: propTypes.object,
  onChange: propTypes.func.isRequired,
  disabled: propTypes.bool,
  accountField: propTypes.node,
};

export default ReturnRelistFields;
